import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { getRecipePhotoUrl } from '../../utils/recipeUtils';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

async function getRecipeDetail(recipeId) {
  const { data, error } = await supabase
    .from('recipes')
    .select('*, recipe_ingredients(*), recipe_steps(*), recipe_photos(*), recipe_meal_moments(*)')
    .eq('id', recipeId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error(t('recipes.geenRecepten'));
  data.recipe_ingredients = (data.recipe_ingredients || []).sort((a, b) => a.volgorde - b.volgorde);
  data.recipe_steps = (data.recipe_steps || []).sort((a, b) => a.stap_nummer - b.stap_nummer);
  data.recipe_photos = (data.recipe_photos || []).sort((a, b) => a.volgorde - b.volgorde);
  return data;
}

// Browser Screen Wake Lock API — silent fallback als de browser dit niet
// ondersteunt (bv. Firefox desktop, oudere Safari): geen foutmelding, gewoon
// geen keep-awake-effect. Alleen actief zolang de kookmodus gemount is en
// het document zichtbaar is (de wake lock wordt door de browser zelf
// vrijgegeven bij het wisselen van tab — daarom de visibilitychange-listener
// om 'm bij terugkeer opnieuw aan te vragen).
function useWakeLock(active) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let sentinel = null;
    let cancelled = false;

    async function request() {
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Silent fallback — geen keep-awake, geen foutmelding.
      }
    }
    request();

    function handleVisibility() {
      if (!cancelled && document.visibilityState === 'visible' && !sentinel) request();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      sentinel?.release?.().catch(() => {});
    };
  }, [active]);
}

export default function RecipeDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode'); // 'kiezen' | 'bereiden' | 'bekijken'
  const mealType = searchParams.get('mealType');
  const logDate = searchParams.get('logDate') || todayISO();
  const saveTarget = searchParams.get('saveTarget'); // 'schedule_entry' → meal_schedule_entries i.p.v. daily_meal_logs
  const scheduleId = searchParams.get('scheduleId');
  const dayOfWeek = searchParams.get('dayOfWeek');
  const { user } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kookmodus, setKookmodus] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setRecipe(await getRecipeDetail(id));
      } catch (err) {
        showError(err.message);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function handleKiezen() {
    setSaving(true);
    try {
      if (saveTarget === 'schedule_entry') {
        const { error } = await supabase.from('meal_schedule_entries').upsert({
          schedule_id: scheduleId, day_of_week: Number(dayOfWeek), meal_type: mealType,
          recipe_id: id, dinner_protein_id: null, dinner_carbs_id: null, dinner_vegetables_id: null,
        }, { onConflict: 'schedule_id,day_of_week,meal_type' });
        if (error) throw error;
        navigate('/client/voeding/weekschema');
      } else {
        const { error } = await supabase.from('daily_meal_logs').upsert({
          user_id: user.id, log_date: logDate, meal_type: mealType,
          recipe_id: id, dinner_protein_id: null, dinner_carbs_id: null, dinner_vegetables_id: null,
          total_calories: recipe.calories, is_planned: false, source: 'manual',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,log_date,meal_type' });
        if (error) throw error;
        navigate('/client/voeding');
      }
    } catch (err) {
      showError(t('recipes.foutOpslaanKeuze') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !recipe) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  if (kookmodus) {
    return <KookModus recipe={recipe} onExit={() => setKookmodus(false)} />;
  }

  const difficultyLabel = recipe.difficulty
    ? t(`recipes.difficulty${recipe.difficulty.charAt(0).toUpperCase()}${recipe.difficulty.slice(1)}`)
    : null;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', paddingBottom: 40 }}>
      <button className="btn btn-ghost" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
        {t('recipes.recipeDetailTerug')}
      </button>

      {recipe.recipe_photos?.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16 }}>
          {recipe.recipe_photos.map((p, i) => (
            <img
              key={p.id}
              src={getRecipePhotoUrl(p.storage_path)}
              alt=""
              style={{ width: 220, height: 160, objectFit: 'cover', borderRadius: 12, flexShrink: 0, cursor: 'pointer' }}
              onClick={() => setViewerIndex(i)}
            />
          ))}
        </div>
      ) : (
        <div style={{ width: '100%', height: 160, borderRadius: 12, marginBottom: 16, background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 32 }}>🍽️</span>
        </div>
      )}

      <h1 style={{ marginBottom: 6 }}>{recipe.name}</h1>
      {recipe.description && <p className="text-muted" style={{ marginBottom: 14 }}>{recipe.description}</p>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {recipe.prep_time_minutes != null && <MetaChip label={t('recipes.prepTime', { min: recipe.prep_time_minutes })} />}
        {recipe.cook_time_minutes != null && <MetaChip label={t('recipes.cookTime', { min: recipe.cook_time_minutes })} />}
        {difficultyLabel && <MetaChip label={difficultyLabel} />}
        {recipe.servings != null && <MetaChip label={t('recipes.servings', { count: recipe.servings })} />}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, marginBottom: 10, textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.04em' }}>{t('recipes.macrosLabel')}</h3>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <MacroStat label={t('recipes.caloriesLabel')} value={`${recipe.calories} kcal`} />
          {recipe.protein != null && <MacroStat label={t('recipes.proteinLabel')} value={`${recipe.protein} g`} />}
          {recipe.carbs != null && <MacroStat label={t('recipes.carbsLabel')} value={`${recipe.carbs} g`} />}
          {recipe.fat != null && <MacroStat label={t('recipes.fatLabel')} value={`${recipe.fat} g`} />}
        </div>
      </div>

      {recipe.recipe_ingredients?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 10, textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.04em' }}>{t('recipes.ingredientsTitle')}</h3>
          {recipe.recipe_ingredients.map(ing => (
            <div key={ing.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--color-primary)', flexShrink: 0 }} />
              <span>{[ing.hoeveelheid, ing.eenheid, ing.naam].filter(Boolean).join(' ')}</span>
            </div>
          ))}
        </div>
      )}

      {recipe.recipe_steps?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 10, textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.04em' }}>{t('recipes.stepsTitle')}</h3>
          {recipe.recipe_steps.map(step => (
            <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{
                width: 22, height: 22, borderRadius: 11, flexShrink: 0, background: 'var(--color-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
              }}>{step.stap_nummer}</span>
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>{step.titel ? `${step.titel} — ` : ''}{step.instructie}</span>
            </div>
          ))}
        </div>
      )}

      {mode === 'kiezen' && (
        <button className="btn btn-primary" style={{ width: '100%' }} disabled={saving} onClick={handleKiezen}>
          {saving ? '…' : t('recipes.chooseAction')}
        </button>
      )}

      {mode === 'bereiden' && recipe.recipe_steps?.length > 0 && (
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setKookmodus(true)}>
          ▶ {t('recipes.startCooking')}
        </button>
      )}

      {viewerIndex !== null && (
        <PhotoViewer photos={recipe.recipe_photos} initialIndex={viewerIndex} onClose={() => setViewerIndex(null)} />
      )}
    </div>
  );
}

function PhotoViewer({ photos, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label={t('recipes.fotoVolledigWeergaveSluiten')}
        style={{
          position: 'absolute', top: 20, right: 20, background: 'none', border: 'none',
          color: '#fff', fontSize: 28, cursor: 'pointer',
        }}
      >
        ✕
      </button>
      <img
        src={getRecipePhotoUrl(photos[index].storage_path)}
        alt=""
        style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
        onClick={e => e.stopPropagation()}
      />
      {photos.length > 1 && (
        <>
          <button
            onClick={e => { e.stopPropagation(); setIndex(i => (i - 1 + photos.length) % photos.length); }}
            style={{ position: 'absolute', left: 20, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }}
          >‹</button>
          <button
            onClick={e => { e.stopPropagation(); setIndex(i => (i + 1) % photos.length); }}
            style={{ position: 'absolute', right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }}
          >›</button>
        </>
      )}
    </div>
  );
}

function MetaChip({ label }) {
  return (
    <span style={{ background: 'var(--color-bg)', borderRadius: 16, padding: '6px 12px', fontSize: 12 }}>{label}</span>
  );
}

function MacroStat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{value}</div>
      <div className="text-muted text-sm">{label}</div>
    </div>
  );
}

function KookModus({ recipe, onExit }) {
  useWakeLock(true);
  const steps = recipe.recipe_steps;
  const [index, setIndex] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(steps[0]?.tijdsduur_seconden ?? null);
  const step = steps[index];

  useEffect(() => {
    setTimerRunning(false);
    setSecondsLeft(step?.tijdsduur_seconden ?? null);
  }, [index]);

  useEffect(() => {
    if (!timerRunning) return;
    if (secondsLeft <= 0) { setTimerRunning(false); return; }
    const id = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timerRunning, secondsLeft]);

  function formatDuration(sec) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={onExit}>✕</button>
        <span className="text-muted text-sm">{t('recipes.stepProgress', { current: index + 1, total: steps.length })}</span>
        <span style={{ width: 32 }} />
      </div>

      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 24, paddingBottom: 4 }}>
        {steps.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setIndex(i)}
            style={{
              width: 32, height: 32, borderRadius: 16, flexShrink: 0, border: 'none', cursor: 'pointer',
              background: i === index ? 'var(--color-primary)' : 'var(--color-bg)',
              color: i === index ? '#fff' : 'inherit', fontWeight: 700, fontSize: 12,
            }}
          >
            {s.stap_nummer}
          </button>
        ))}
      </div>

      <div className="card" style={{ textAlign: 'center', padding: 32, marginBottom: 24 }}>
        {step.titel && <p className="text-muted" style={{ marginTop: 0 }}>{step.titel}</p>}
        <p style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.4 }}>{step.instructie}</p>

        {step.tijdsduur_seconden != null && (
          <div style={{ marginTop: 20, display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10, border: '1px solid var(--color-border)', borderRadius: 14, padding: 18, minWidth: 180 }}>
            <span style={{ fontSize: 30, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatDuration(secondsLeft ?? step.tijdsduur_seconden)}</span>
            <button
              className="btn btn-sm"
              style={{ background: timerRunning ? '#e24b4a' : 'var(--color-primary)', color: '#fff', border: 'none' }}
              onClick={() => {
                if (!timerRunning && (secondsLeft == null || secondsLeft <= 0)) setSecondsLeft(step.tijdsduur_seconden);
                setTimerRunning(r => !r);
              }}
            >
              {timerRunning ? t('recipes.timerStop') : t('recipes.timerStart')}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} disabled={index === 0} onClick={() => setIndex(i => i - 1)}>
          {t('recipes.stepPrev')}
        </button>
        {index === steps.length - 1 ? (
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onExit}>{t('recipes.stepDone')}</button>
        ) : (
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setIndex(i => i + 1)}>{t('recipes.stepNext')}</button>
        )}
      </div>
    </div>
  );
}
